import { ChangeDetectionStrategy, Component } from '@angular/core'
import { StatusContext } from 'src/app/account/components/app-resources-view/app-resources-view.component'
import { Application } from 'src/app/account/services/application.service'
import { SyncPhaseDto } from 'src/app/services/dtos'
import {
  RECOVERY_SCREEN_TABS,
  RecoveryScreenScenario,
  buildRecoveryScreenScenarios,
} from '../../mocks/showcase-recovery-screen-mocks'

/**
 * Composed Recovery Environment / Failback snapshots. Each scenario is a fully
 * realized state — application, resources, status and notice fields — so the
 * section renders the whole screen without orchestrating a real recovery.
 */
@Component({
  selector: 'showcase-recovery-screen',
  templateUrl: './showcase-recovery-screen.component.html',
  styleUrls: ['./showcase-recovery-screen.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ShowcaseRecoveryScreenComponent {
  readonly StatusContext = StatusContext
  readonly recoveryScreenScenarios: ReadonlyArray<RecoveryScreenScenario> = buildRecoveryScreenScenarios()
  recoveryScreenScenarioKey: string = this.recoveryScreenScenarios[0].key

  get recoveryScreenActiveScenario(): RecoveryScreenScenario {
    return (
      this.recoveryScreenScenarios.find((s) => s.key === this.recoveryScreenScenarioKey) ??
      this.recoveryScreenScenarios[0]
    )
  }

  // Convenience getters bound directly in the template so the screen markup
  // mirrors what application.component.html does with its component getters.
  get screenApp(): Application {
    return this.recoveryScreenActiveScenario.application
  }
  get screenResources() {
    return this.recoveryScreenActiveScenario.resources
  }
  get screenStatus() {
    return this.recoveryScreenActiveScenario.status
  }
  get screenNotice() {
    return this.recoveryScreenActiveScenario.operationNotice
  }
  get screenFailbackNoticeText(): string | null {
    return this.recoveryScreenActiveScenario.failbackOperationNoticeText
  }
  get screenIsFailbackTab(): boolean {
    return this.recoveryScreenActiveScenario.tab === 'failback'
  }

  // Mirrors application.component's failback visibility: the Recovery/Failback
  // sub-tab chips only appear in FAILOVER phase, entitled (assumed here), and when
  // resources aren't quarantined.
  get screenShowsFailbackSubTabs(): boolean {
    const scenario = this.recoveryScreenActiveScenario
    return scenario.syncPhase === SyncPhaseDto.FAILOVER && !scenario.tabBarFlags.showUnquarantine
  }

  get screenTabType() {
    return this.screenIsFailbackTab ? RECOVERY_SCREEN_TABS.failback : RECOVERY_SCREEN_TABS.recovery
  }

  // Grouped for the picker <optgroup>s — preserves source order so
  // STANDBY > FAILOVER > FAILOVER_TEST > CLEANUP > FAILBACK reads top-to-bottom.
  get recoveryScreenScenarioGroups(): { group: string; label: string; scenarios: RecoveryScreenScenario[] }[] {
    const labelFor: Record<RecoveryScreenScenario['group'], string> = {
      STANDBY: 'Recovery — Standby',
      FAILOVER: 'Recovery — Failover',
      FAILOVER_TEST: 'Recovery — Failover Test',
      CLEANUP: 'Recovery — Cleanup',
      FAILBACK: 'Failback',
    }
    const groups: { group: string; label: string; scenarios: RecoveryScreenScenario[] }[] = []
    for (const scenario of this.recoveryScreenScenarios) {
      const existing = groups.find((g) => g.group === scenario.group)
      if (existing) existing.scenarios.push(scenario)
      else groups.push({ group: scenario.group, label: labelFor[scenario.group], scenarios: [scenario] })
    }
    return groups
  }

  setRecoveryScreenScenario(key: string): void {
    if (this.recoveryScreenScenarioKey === key) return
    this.recoveryScreenScenarioKey = key
  }
}
